import { useCurrentSite } from '@hotjar/site';
import { useEffect, useState, useCallback } from 'react';
import { useNotify } from '@hotjar/hotsauce';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { OVERVIEW, SURVEYS } from '@hotjar/webapp/constants/routes';
import { browserHistory } from '@hotjar/webapp/utils/routing/browser-history';
import { getParameter } from '@hotjar/insights-common/lib/url';
import { useQueryParameter } from '@hotjar/webapp/hooks/useQueryParameter';
import { useSurveyTemplate } from './useSurveyTemplate';
import { STEPS } from '../steps';

interface SetupWizardNavigation {
  step: string | undefined;
  isLoading: boolean;
  goToStep: (step: string) => void;
  goToInsights: () => void;
}

function isValidStep(step?: STEPS): boolean {
  return step ? Object.values(STEPS).includes(step) : false;
}

export const useSetupWizardNavigation = (): SetupWizardNavigation => {
  const { data: currentSite } = useCurrentSite();
  const stepFromUrl = useQueryParameter('step');
  const gtmToken = useQueryParameter('gtm_token');
  const nextUrl = useQueryParameter('next');
  const [step, setStep] = useState<string | undefined>(() =>
    isValidStep(stepFromUrl as STEPS) ? stepFromUrl : STEPS.INSTALLATION_METHOD,
  );
  const { data: templateId, isLoading, isFetched } = useSurveyTemplate();
  const { showToast } = useNotify();

  const siteId = currentSite?.id;
  const goToStep = useCallback(
    (nextStep) => {
      const currentStep = getParameter(window.location.href, 'step');
      if (nextStep && siteId && currentStep !== nextStep) {
        setStep(nextStep);
        browserHistory.push(
          `${getRoute(OVERVIEW.SETUP.path, { siteId: siteId as number })}?step=${nextStep}`,
        );
      }
    },
    [siteId],
  );
  const goToInsights = useCallback(() => {
    if (nextUrl) {
      browserHistory.replace(nextUrl);
    } else if (templateId) {
      browserHistory.push(
        getRoute(SURVEYS.CREATE.path, {
          templateId: String(templateId),
          siteId: siteId as number,
          activeFlowSection: 'name',
        }),
      );
    } else {
      browserHistory.push(getRoute(OVERVIEW.BASE.path, { siteId: siteId as number }));
    }
  }, [siteId, templateId, nextUrl]);

  useEffect(() => {
    if (gtmToken === 'installed') {
      goToStep(STEPS.GTM);
      showToast({ kind: 'success', text: 'GTM integration connected.' });
    } else if (gtmToken) {
      showToast({ kind: 'danger', text: `Failed to connect to GTM: ${gtmToken}` });
    }
  }, [gtmToken, showToast, goToStep]);

  useEffect(() => {
    const unlisten = browserHistory.listen(() => {
      const currentStep = getParameter(window.location.href, 'step') || STEPS.INSTALLATION_METHOD;
      if (currentStep !== step) {
        setStep(currentStep);
      }
    });

    return () => unlisten();
  }, [setStep, step]);

  return { step, isLoading: isLoading || !isFetched, goToStep, goToInsights };
};
